/*
 * formsec.js -- klientska kontrola poptavkoveho formulare (rozhodnuti
 * majitele 8. 9. 2026).
 *
 * ZADANI DOSLOVA: "ve formularich vyzaduj email i telefon, pokud neco
 * z toho nevyplni, musis vyrolovat zpet na ta pole, ktera nebyla
 * vyplnena, a musi byt jasne oznacena (treba oranzovy okraj), po odeslani
 * musi byt presmerovan na dekovnou stranku."
 *
 * TENHLE MODUL JE POHODLI, NE BEZPECNOST. Zavaznou validaci dela server
 * (`terrahunt_inquiry_form_config()` v pluginu): odeslani s vypnutym JS,
 * z curlu nebo s odstranenym atributem `required` skonci uplne stejne,
 * jen o jedno presmerovani dal (PRG chybova vetev). Kdyby se tenhle
 * soubor nenacetl vubec, formular zustane pouzitelny -- proto zadna
 * inicializace neni podminkou odeslani a kazda vetev fail-open vraci.
 *
 * PROC VLASTNI KONTROLA A NE NATIVNI `required` BUBLINA:
 *   - nativni hlaska je nestylovatelna a mizi po par vterinach,
 *   - ukaze VZDY JEN JEDNU (prvni) chybu, ne vsechny naraz,
 *   - je v jazyce PROHLIZECE, ne stranky: anglicky Chrome by ceskemu
 *     navstevnikovi napsal "Please fill out this field".
 * Formular proto nese `novalidate` (formsec.php) a hlasky si bere
 * z registru Textu webu pres atributy `data-th-msg-*` na kazdem poli --
 * skript sam nezna ani jedno slovo copy.
 *
 * ZIVY WEB TO DELA STEJNE. Mirrorovany bundle stare sablony
 * (`wp-content-theme/terrahunt/assets/js/main.js`) je jQuery validate:
 * take si nastavuje `novalidate`, take znaci vadne pole vlastni tridou
 * (`input-validation-error`, cerveny ramecek v `assets/css/main.css`),
 * take pise hlasku pod pole (`.field-validation-error`) a take posila
 * fokus na prvni vadne pole (`focusInvalid: true`). Tohle je prenos te
 * same funkce do sablony 2026, jen s jejimi tokeny a bez jQuery.
 *
 * KONTRAKT S MARKUPEM (`template-parts/components/formsec.php`):
 *   `.formsec-form`                   -- formular, ktery se hlida
 *   `[required]`                      -- pole, ktera musi byt vyplnena
 *   `data-th-msg-required`            -- hlaska "pole je prazdne"
 *   `data-th-msg-format`              -- hlaska "spatny tvar" (e-mail, telefon)
 *   `#{id}-error`                     -- odstavec pro hlasku pod polem
 *   `data-formsec-error-fields`       -- seznam poli oznacenych SERVEREM
 *   trida `is-invalid`                -- oranzovy ramecek (components.css)
 */

/* Trida, kterou znaci vadne pole i PHP -- jedno misto pro obe strany. */
const INVALID = 'is-invalid';

const MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Chce si uzivatel odpustit pohyb?
 *
 * @return {boolean} true pri `prefers-reduced-motion: reduce`.
 */
function reduceMotion() {
	return !!(window.matchMedia && window.matchMedia(MOTION_QUERY).matches);
}

/**
 * Je e-mail aspon v pouzitelnem tvaru?
 *
 * ZAMERNE VOLNE, a schvalne volnejsi nez server. Server rozhoduje
 * (`is_email()`); tahle kontrola ma chytit preklep typu chybejici zavinac
 * driv, nez navstevnik cekà na round-trip. Prisnejsi klient nez server by
 * odmitl adresu, kterou by server prijal -- a to je ztracena poptavka,
 * ne kvalita dat.
 *
 * @param {string} value Hodnota pole.
 * @return {boolean}
 */
function emailPlausible(value) {
	const v = String(value).trim();

	if (v.indexOf(' ') !== -1) {
		return false;
	}

	const at = v.indexOf('@');

	if (at < 1 || at !== v.lastIndexOf('@')) {
		return false;
	}

	const domain = v.slice(at + 1);

	return domain.indexOf('.') > 0 && domain.charAt(domain.length - 1) !== '.';
}

/**
 * Je telefon aspon v pouzitelnem tvaru?
 *
 * TATAZ PRAVIDLA JAKO NA SERVERU (`terrahunt_inquiry_phone_plausible()`
 * v pluginu): povolene znaky (cislice, mezera, `+`, `-`, `/`, `.`,
 * zavorky) a 9 az 20 cislic. Kdyby se obe strany rozesly, dostal by
 * navstevnik chybu az po odeslani na poli, ktere mu klient odsouhlasil --
 * nebo naopak neprosel formular, ktery server prijme.
 *
 * ZADNA PRISNEJSI REGULARKA. Telefon nema kanonicky zapis; lide ho pisi
 * s predvolbou i bez, s mezerami, lomitky i zavorkami. Prisna kontrola
 * v poptavkovem formulari je past na platna cisla, ne ochrana dat.
 *
 * @param {string} value Hodnota pole.
 * @return {boolean}
 */
function phonePlausible(value) {
	const v = String(value).trim();

	if (!/^[0-9+\-/.() ]+$/.test(v)) {
		return false;
	}

	const digits = v.replace(/\D+/g, '');

	return digits.length >= 9 && digits.length <= 20;
}

/**
 * Odstavec s hlaskou pod polem. Markup ho vykresluje vzdycky (i prazdny),
 * takze se tu nic netvori -- kdyby chybel, vrati se `null` a znacka pole
 * se vypise i tak (fail-open: oranzovy ramecek je porad lepsi nez nic).
 *
 * @param {Element} field Vstupni pole.
 * @return {Element|null}
 */
function errorBox(field) {
	const id = field.getAttribute('id');

	return id ? document.getElementById(id + '-error') : null;
}

/**
 * Oznaci pole jako vadne a vypise k nemu hlasku.
 *
 * @param {Element} field Vstupni pole.
 * @param {string}  msg   Hlaska (prazdna = jen ramecek, bez textu).
 * @return {void}
 */
function markInvalid(field, msg) {
	field.classList.add(INVALID);
	field.setAttribute('aria-invalid', 'true');

	const box = errorBox(field);

	if (!box) {
		return;
	}

	box.textContent = msg;

	if (msg) {
		field.setAttribute('aria-describedby', box.id);
	}
}

/**
 * Zrusi znacku pole. Vola se pri kazde uprave hodnoty: kdo uz chybu
 * opravuje, nema pred sebou koukat na cerveny (tady oranzovy) ramecek do
 * dalsiho odeslani.
 *
 * @param {Element} field Vstupni pole.
 * @return {void}
 */
function clearInvalid(field) {
	if (!field.classList.contains(INVALID)) {
		return;
	}

	field.classList.remove(INVALID);
	field.removeAttribute('aria-invalid');

	const box = errorBox(field);

	if (box) {
		box.textContent = '';

		if (field.getAttribute('aria-describedby') === box.id) {
			field.removeAttribute('aria-describedby');
		}
	}
}

/**
 * Sroluje na pole tak, aby bylo uprostred vyrezu.
 *
 * `block: 'center'`, ne `'start'`: horni hranu vyrezu prekryva lepiva
 * hlavicka a spodni na mobilu spodni lista (`dock`), takze pole zarovnane
 * na kraj muze skoncit pod nimi. Stred je jedina poloha, ktera vyjde
 * v obou pripadech.
 *
 * @param {Element} field Vstupni pole.
 * @return {void}
 */
function scrollToField(field) {
	if (!field.scrollIntoView) {
		return;
	}

	field.scrollIntoView({
		block: 'center',
		inline: 'nearest',
		behavior: reduceMotion() ? 'auto' : 'smooth',
	});
}

/**
 * Zkontroluje jedno pole. Vraci hlasku k vypsani, nebo prazdny retezec,
 * kdyz je pole v poradku.
 *
 * Poradi kontrol je pevne: PRAZDNE pole se hlasi jako prazdne, ne jako
 * spatny tvar. Opacne poradi by u prazdneho telefonu vypsalo "Zadejte
 * prosim telefon v platnem tvaru", coz je na prazdnem poli nesmysl.
 *
 * @param {Element} field Vstupni pole.
 * @return {string} Hlaska, nebo '' kdyz je pole v poradku.
 */
function checkField(field) {
	const value = String(field.value || '').trim();
	const type = String(field.getAttribute('type') || '').toLowerCase();

	if (field.hasAttribute('required') && value === '') {
		return field.getAttribute('data-th-msg-required') || '';
	}

	if (value === '') {
		return '';
	}

	if (type === 'email' && !emailPlausible(value)) {
		return field.getAttribute('data-th-msg-format') || '';
	}

	if (type === 'tel' && !phonePlausible(value)) {
		return field.getAttribute('data-th-msg-format') || '';
	}

	return '';
}

/**
 * Pole formulare, ktera tenhle modul hlida: povinna (`required`) plus
 * e-mail a telefon, u kterych se kontroluje i tvar.
 *
 * @param {Element} form Formular.
 * @return {Element[]}
 */
function watchedFields(form) {
	const list = form.querySelectorAll('[required], input[type="email"], input[type="tel"]');
	const out = [];

	for (let i = 0; i < list.length; i += 1) {
		/*
		 * Skryta pole (`th_js`, `th_elapsed`, honeypot) do kontroly
		 * nepatri: navstevnik je nevidi a nemuze je opravit, takze by
		 * chyba na nich formular zamkla natrvalo.
		 */
		if (list[i].type !== 'hidden') {
			out.push(list[i]);
		}
	}

	return out;
}

/**
 * Napoji kontrolu na jeden formular.
 *
 * @param {Element} form Formular `.formsec-form`.
 * @return {void}
 */
function initForm(form) {
	const fields = watchedFields(form);

	if (!fields.length) {
		return;
	}

	/*
	 * `novalidate` vykresluje uz PHP; tady je to POJISTKA pro stranky
	 * ulozene do cache jeste pred touhle zmenou. Bez nej by prohlizec
	 * ukazal svou vlastni bublinu driv, nez se dostane ke slovu tenhle
	 * submit listener, a navstevnik by videl dve ruzne hlasky o teze
	 * chybe.
	 */
	form.setAttribute('novalidate', 'novalidate');

	fields.forEach(function (field) {
		field.addEventListener('input', function () {
			clearInvalid(field);
		});

		field.addEventListener('change', function () {
			clearInvalid(field);
		});
	});

	form.addEventListener('submit', function (event) {
		let first = null;

		fields.forEach(function (field) {
			const msg = checkField(field);

			if (msg === '' && !field.classList.contains(INVALID)) {
				return;
			}

			if (msg === '') {
				clearInvalid(field);

				return;
			}

			markInvalid(field, msg);

			if (first === null) {
				first = field;
			}
		});

		if (first === null) {
			return;
		}

		event.preventDefault();

		/*
		 * FOKUS PRVNI, SCROLL DRUHY -- a fokus s `preventScroll`.
		 * `focus()` sam o sobe sroluje skokem, cimz by prepsal plynule
		 * `scrollIntoView` o radek niz (v Safari se to projevi tak, ze
		 * plynuly pohyb vubec nezacne). Prohlizec, ktery `preventScroll`
		 * nezna, argument proste ignoruje -- chova se jako driv, tedy
		 * porad spravne, jen bez animace. Zadny try/catch: `focus()`
		 * s objektem nikde nevyhazuje.
		 */
		first.focus({ preventScroll: true });

		scrollToField(first);
	});

	/*
	 * SERVEROVY NAVRAT (`?th_status=error&th_resume=...`). Pole uz jsou
	 * oznacena z PHP i s hlaskami; skript jen dorolovat na to prvni.
	 *
	 * FOKUS SE TU NENASTAVUJE. Na mobilu by vyskocila klavesnice jeste
	 * driv, nez si navstevnik precte, co po nem web chce -- a hlaska nad
	 * formularem i veta pod polem by mu zmizely pod ni. Na klientske
	 * vetvi vys je fokus naopak spravne: tam uzivatel prave klikl na
	 * odeslat a chce psat dal.
	 */
	const marked = String(form.getAttribute('data-formsec-error-fields') || '').trim();

	if (marked === '') {
		return;
	}

	const firstMarked = form.querySelector('.' + INVALID);

	if (!firstMarked) {
		return;
	}

	/*
	 * Az po `load`: obrazky a mapy nad formularem se dopocitavaji a
	 * posunuly by cil scrollu o stovky pixelu. `requestAnimationFrame`
	 * navic pocka na prvni vykresleni po dopocitani rozvrzeni.
	 */
	const go = function () {
		requestAnimationFrame(function () {
			scrollToField(firstMarked);
		});
	};

	if (document.readyState === 'complete') {
		go();
	} else {
		window.addEventListener('load', go);
	}
}

/**
 * Inicializace modulu.
 *
 * @return {void}
 */
export function init() {
	const forms = document.querySelectorAll('.formsec-form');

	if (!forms.length) {
		return;
	}

	forms.forEach(function (form) {
		initForm(form);
	});
}
